import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';

export interface PortalPopoverPos {
  top: number;
  left: number;
}

export interface UsePortalPopoverOptions {
  /** Close the panel when the page scrolls (but not when scrolling inside the panel). */
  closeOnScroll?: boolean;
}

export interface UsePortalPopoverReturn {
  open: boolean;
  pos: PortalPopoverPos | null;
  triggerRef: RefObject<HTMLButtonElement>;
  panelRef: RefObject<HTMLDivElement>;
  toggle: () => void;
  close: () => void;
}

/**
 * Shared logic for portal-based popovers that use createPortal + position: fixed.
 * Handles open state, position capture, click-outside, resize, and optional scroll-to-close.
 */
export function usePortalPopover({ closeOnScroll = false }: UsePortalPopoverOptions = {}): UsePortalPopoverReturn {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<PortalPopoverPos | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const toggle = useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, left: rect.left });
    }
    setOpen(o => !o);
  }, []);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        panelRef.current && !panelRef.current.contains(e.target as Node) &&
        triggerRef.current && !triggerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };

    const handleResize = () => setOpen(false);

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', handleResize);

    let handleScroll: ((e: Event) => void) | undefined;
    if (closeOnScroll) {
      handleScroll = (e: Event) => {
        if (panelRef.current?.contains(e.target as Node)) return;
        setOpen(false);
      };
      window.addEventListener('scroll', handleScroll, true);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', handleResize);
      if (handleScroll) window.removeEventListener('scroll', handleScroll, true);
    };
  }, [open, closeOnScroll]);

  return { open, pos, triggerRef, panelRef, toggle, close };
}
